import { storeName } from "../lib/labels";
import { comparePhrase, compareKind } from "../lib/compare";
import { useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, Minus, ChevronDown, ArrowUpRight } from "lucide-react";

function fmtPct(v) {
  if (v == null) return "—";
  const s = v > 0 ? "+" : "";
  return `${s}${v}%`;
}

function DeltaRow({ label, value, start, end, dark }) {
  const positive = value != null && value > 0;
  const negative = value != null && value < 0;
  const Icon = value == null ? Minus : positive ? TrendingUp : negative ? TrendingDown : Minus;
  const color = value == null
    ? "text-neutral-500"
    : positive
    ? (dark ? "text-emerald-400" : "text-emerald-700")
    : (dark ? "text-[#ff6b6b]" : "text-[#c72c2c]");
  const kind = compareKind(label);
  const phrase = comparePhrase(kind, start, end);
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-t border-current/10">
      <div className="min-w-0">
        <div className="bm-eyebrow opacity-70 truncate">{label}</div>
        <div className="text-[10px] font-mono-bm opacity-50 mt-0.5 truncate">{phrase}</div>
      </div>
      <div className={`font-mono-bm text-sm font-semibold whitespace-nowrap flex items-center gap-1 ${color}`}>
        <Icon size={13} />
        {fmtPct(value)}
      </div>
    </div>
  );
}

// Format a raw metric value for display in the per-store breakdown row
function fmtMetric(key, v) {
  if (v == null) return "—";
  if (key === "revenue" || key === "aov") return `$${Math.round(v).toLocaleString()}`;
  if (key === "transactions") return Number(v).toLocaleString();
  if (key === "pcs_per_order") return Number(v).toFixed(2);
  return String(v);
}

function StoreRow({ o, valueKey, deltaKey, dark }) {
  const val = o[valueKey];
  const growth = o[deltaKey];
  const positive = growth != null && growth > 0;
  const negative = growth != null && growth < 0;
  const color = growth == null
    ? "text-neutral-500"
    : positive
    ? (dark ? "text-emerald-400" : "text-emerald-700")
    : (dark ? "text-[#ff6b6b]" : "text-[#c72c2c]");
  return (
    <Link
      to={`/store/${o.outlet_id}`}
      data-testid={`store-row-${storeName(o.name)}`}
      className="group flex items-baseline justify-between gap-3 py-2 border-t border-current/10 hover:bg-[#0a0a0a]/5 -mx-2 px-2 cursor-pointer transition-colors"
    >
      <div className="text-sm truncate flex items-center gap-1">
        {storeName(o.name)}
        <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-60 transition-opacity" />
      </div>
      <div className="flex items-baseline gap-3 whitespace-nowrap">
        <span className="font-mono-bm text-sm font-semibold tabular-nums">{fmtMetric(valueKey, val)}</span>
        <span className={`font-mono-bm text-xs ${color} w-14 text-right`}>{fmtPct(growth)}</span>
      </div>
    </Link>
  );
}

export function BigMetricCard({ eyebrow, value, subValue, comparisons, byOutlet, deltaKey, valueKey, accent, testId, hint, compareMode = "same_period" }) {
  const [expanded, setExpanded] = useState(false);

  // The per-store breakdown follows the global Comparison picker so its growth
  // numbers match the Store-vs-store panel and the header comparison rows —
  // rather than always comparing to the immediately-prior period.
  const storeDeltaKey =
    compareMode === "same_dates"
      ? `yoy_dates_growth_pct_${valueKey}`
      : compareMode === "same_period"
      ? `yoy_growth_pct_${valueKey}`
      : `growth_pct_${valueKey}`;
  const compareLabel =
    compareMode === "same_dates"
      ? "vs LY dates"
      : compareMode === "same_period"
      ? "vs LY"
      : "vs prior";
  return (
    <div
      data-testid={testId}
      className={`p-6 border border-[#0a0a0a] bg-[#f5f2eb] flex flex-col ${accent ? "border-l-4 border-l-[#c72c2c]" : ""}`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="bm-eyebrow opacity-70">{eyebrow}</div>
        {hint && <div className="bm-eyebrow opacity-40">{hint}</div>}
      </div>
      <div className="bm-headline text-3xl mt-2 tabular-nums leading-none tracking-tight">
        {value}
      </div>
      {subValue && <div className="mt-2 text-xs font-mono-bm opacity-70">{subValue}</div>}

      <div className="mt-5 space-y-0">
        {comparisons?.map((c, i) => (
          <DeltaRow
            key={i}
            label={c.label}
            value={c[deltaKey]}
            start={c.start}
            end={c.end}
            dark={false}
          />
        ))}
      </div>

      <button
        onClick={() => setExpanded((v) => !v)}
        data-testid={`${testId}-expand`}
        className="mt-6 -mx-6 -mb-6 px-6 py-4 border-t border-[#0a0a0a]/20 bm-eyebrow flex items-center justify-between text-neutral-600 hover:text-[#0a0a0a] hover:bg-[#ede8dc]"
      >
        <span>By store</span>
        <ChevronDown size={14} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>

      {expanded && (
        <div className="-mx-6 -mb-6 px-6 pt-1 pb-5 bg-[#ede8dc] border-t border-[#0a0a0a]/20" data-testid={`${testId}-breakdown`}>
          <div className="flex items-baseline justify-between bm-eyebrow opacity-60 pt-2 pb-1">
            <span>Store</span>
            <span>Value &nbsp; · &nbsp; {compareLabel}</span>
          </div>
          {byOutlet?.map((o) => (
            <StoreRow
              key={o.outlet_id}
              o={o}
              valueKey={valueKey}
              deltaKey={storeDeltaKey}
              dark={false}
            />
          ))}
        </div>
      )}
    </div>
  );
}
